import type { VoiceCommandResult } from './voiceCommandClient';
import { VOICE_PENDING_TTL, VOICE_STORAGE_SUFFIXES } from '../voice/constants';
import {
  isExpired,
  migrateLegacyVoiceStorage,
  readVoiceKey,
  removeVoiceKey,
  resolveVoiceStorageScope,
  writeVoiceKey,
} from '../voice/voiceStorage';

export interface PendingContactConfirmation {
  intent: string;
  entityKey: string;
  extractedName: string;
  suggestedContact: {
    id: number;
    company_name?: string | null;
    contact_name?: string | null;
  };
  pendingEntities?: Record<string, unknown>;
  createdAt?: number;
  expiresAt?: number;
}

export interface PendingContactDisambiguation {
  intent: string;
  entityKey: string;
  searchTerm: string;
  matchingContacts: Array<{
    id: number;
    company_name?: string | null;
    contact_name?: string | null;
  }>;
  pendingEntities?: Record<string, unknown>;
  createdAt?: number;
  expiresAt?: number;
}

interface PendingActionBundle {
  confirmation?: PendingContactConfirmation | null;
  disambiguation?: PendingContactDisambiguation | null;
  createdAt?: number;
  expiresAt?: number;
}

const AFFIRMATIVE = /^(yes|yeah|yep|yup|correct|right|sure|ok|okay|confirm|confirmed|go ahead|do it)$/i;
const NEGATIVE = /^(no|nope|nah|wrong|incorrect|not that|different)$/i;

function scopeOrNull() {
  const scope = resolveVoiceStorageScope();
  if (scope) migrateLegacyVoiceStorage(scope);
  return scope;
}

function readBundle(): PendingActionBundle | null {
  const scope = scopeOrNull();
  if (!scope) return null;
  const bundle = readVoiceKey<PendingActionBundle>(
    scope,
    VOICE_STORAGE_SUFFIXES.pendingAction
  );
  if (!bundle) return null;
  if (isExpired(bundle)) {
    removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.pendingAction);
    return null;
  }
  return bundle;
}

function writeBundle(bundle: PendingActionBundle): void {
  const scope = scopeOrNull();
  if (!scope) return;
  const createdAt = bundle.createdAt || Date.now();
  const expiresAt =
    bundle.expiresAt || createdAt + VOICE_PENDING_TTL.DEFAULT_MS;
  writeVoiceKey(
    scope,
    VOICE_STORAGE_SUFFIXES.pendingAction,
    { ...bundle, createdAt, expiresAt },
    VOICE_PENDING_TTL.DEFAULT_MS
  );
}

export function isAffirmativeReply(text: string): boolean {
  return AFFIRMATIVE.test(text.trim());
}

export function isNegativeReply(text: string): boolean {
  return NEGATIVE.test(text.trim());
}

export function storePendingContactConfirmation(result: VoiceCommandResult): void {
  if (result.phase !== 'contact_confirmation' || !result.data?.suggested_contact) return;

  const createdAt = Date.now();
  const pending: PendingContactConfirmation = {
    intent: result.data.intent || 'create_quote',
    entityKey: result.data.entity_key || 'customer_name',
    extractedName: result.data.extracted_name || '',
    suggestedContact: result.data.suggested_contact,
    pendingEntities: result.data.pending_entities,
    createdAt,
    expiresAt: createdAt + VOICE_PENDING_TTL.DEFAULT_MS,
  };

  const existing = readBundle() || {};
  writeBundle({
    ...existing,
    confirmation: pending,
    disambiguation: null,
    createdAt,
    expiresAt: pending.expiresAt,
  });
}

export function storePendingContactDisambiguation(result: VoiceCommandResult): void {
  if (result.phase !== 'disambiguation' || !Array.isArray(result.data?.matching_customers)) return;

  const createdAt = Date.now();
  const pending: PendingContactDisambiguation = {
    intent: result.data.intent || 'create_quote',
    entityKey: result.data.entity_key || 'customer_name',
    searchTerm: result.data.search_term || '',
    matchingContacts: result.data.matching_customers,
    pendingEntities: result.data.pending_entities,
    createdAt,
    expiresAt: createdAt + VOICE_PENDING_TTL.DEFAULT_MS,
  };

  const existing = readBundle() || {};
  writeBundle({
    ...existing,
    disambiguation: pending,
    confirmation: null,
    createdAt,
    expiresAt: pending.expiresAt,
  });
}

export function getPendingContactConfirmation(): PendingContactConfirmation | null {
  const bundle = readBundle();
  const pending = bundle?.confirmation || null;
  if (!pending) return null;
  if (isExpired(pending)) {
    clearPendingContactConfirmation();
    return null;
  }
  return pending;
}

export function getPendingContactDisambiguation(): PendingContactDisambiguation | null {
  const bundle = readBundle();
  const pending = bundle?.disambiguation || null;
  if (!pending) return null;
  if (isExpired(pending)) {
    clearPendingContactDisambiguation();
    return null;
  }
  return pending;
}

export function clearPendingContactConfirmation(): void {
  const scope = scopeOrNull();
  if (!scope) return;
  const bundle = readBundle();
  if (!bundle) return;
  writeBundle({ ...bundle, confirmation: null });
}

export function clearPendingContactDisambiguation(): void {
  const scope = scopeOrNull();
  if (!scope) return;
  const bundle = readBundle();
  if (!bundle) return;
  writeBundle({ ...bundle, disambiguation: null });
}

export function clearAllPendingContactFlows(): void {
  const scope = scopeOrNull();
  if (scope) removeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.pendingAction);
  // Cross-tab / legacy safety
  try {
    localStorage.removeItem('voicePendingContactConfirmation');
    localStorage.removeItem('voicePendingContactDisambiguation');
    localStorage.removeItem('voicePendingCommandId');
  } catch {
    /* ignore */
  }
}
